import React, { useState } from "react";
import { Button } from "../../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../../design/dialog";
import { Input } from "../../../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../../../design/fieldset";
import { Timestamp } from "firebase/firestore";
import { useChild } from "../../../../../contexts/ChildContext";
import { useCase } from "../../../../../contexts/CaseContext";
import { Switch, SwitchField } from "../../../../../design/switch";
import { Textarea } from "../../../../../design/textarea";

interface Props {
  caseId: string;
}

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  dob?: string;
}

const CreateUserButton: React.FC<Props> = ({ caseId }) => {
  const { createChild } = useChild();
  const { setSelectedCase } = useCase();
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [hasSpecialNeeds, setHasSpecialNeeds] = useState<boolean>(false);
  const [allergies, setAllergies] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!firstName.trim()) {
      newErrors.firstName = "First name is required.";
    }
    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required.";
    }
    if (!dob) {
      newErrors.dob = "Date of birth is required.";
    } else if (isNaN(Date.parse(dob))) {
      newErrors.dob = "Invalid date format.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      await createChild({
        firstName,
        lastName,
        dob: Timestamp.fromDate(new Date(dob)),
        caseId,
        hasSpecialNeeds,
        allergies,
        notes,
      });
      setSelectedCase(caseId);
      clearForm();
    } catch (error) {
      console.error("Error creating location:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Child
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen} size="2xl">
        <DialogTitle>Create Child</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={dob}
                  placeholder="Date of Birth"
                  onChange={(e) => setDob(e.target.value)}
                />
                {errors.dob && <ErrorMessage>{errors.dob}</ErrorMessage>}
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Allergies</Label>
                <Input
                  type="text"
                  value={allergies}
                  placeholder="Allergies"
                  onChange={(e) => setAllergies(e.target.value)}
                />
              </Field>
            </FieldGroup>
            <FieldGroup>
              <SwitchField>
                <Label>Has Special Needs</Label>
                <Switch
                  checked={hasSpecialNeeds}
                  onChange={() => setHasSpecialNeeds(!hasSpecialNeeds)}
                />
              </SwitchField>
            </FieldGroup>

            <FieldGroup>
              <Field>
                <Label>Notes</Label>
                <Textarea
                  rows={5}
                  value={notes}
                  placeholder="Notes"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateUserButton;
